import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import showToast from '@/components/ui/Toast';
import { getClientProfile, updateClientProfile, DataProfile } from '@/services/clientProfile';
import SEO from '@/components/common/SEO';
import ClientRacketStatsSettingsPage from './ClientRacketStatsSettingsPage';

export interface MetricDef {
  key: string;
  label: string;
  icon: string;
  bg: string;
  text: string;
  getValue: (statItem: any) => number | string;
}

export const ALL_METRICS_LIST: MetricDef[] = [
  { key: 'motm', label: 'MOTM', icon: 'ph:star-bold', bg: 'bg-amber-50', text: 'text-amber-500', getValue: (s) => s?.motm ?? 0 },
  { key: 'gol', label: 'Gol', icon: 'ph:soccer-ball-bold', bg: 'bg-orange-50', text: 'text-[#FC480D]', getValue: (s) => s?.goals ?? s?.gol ?? 0 },
  { key: 'assist', label: 'Assist', icon: 'ph:handshake-bold', bg: 'bg-amber-50', text: 'text-amber-600', getValue: (s) => s?.assists ?? s?.assist ?? 0 },
  { key: 'key_pass', label: 'Key Pass', icon: 'ph:target-bold', bg: 'bg-blue-50', text: 'text-blue-600', getValue: (s) => s?.key_passes ?? s?.key_pass ?? 0 },
  { key: 'cleansheet', label: 'Cleansheet', icon: 'ph:shield-bold', bg: 'bg-emerald-50', text: 'text-emerald-600', getValue: (s) => s?.clean_sheets ?? s?.cleansheet ?? 0 },
  { key: 'save', label: 'Save', icon: 'ph:hand-palm-bold', bg: 'bg-orange-50', text: 'text-orange-600', getValue: (s) => s?.saves ?? s?.save ?? 0 },
  { key: 'clearance', label: 'Clearance', icon: 'ph:shield-check-bold', bg: 'bg-indigo-50', text: 'text-indigo-600', getValue: (s) => s?.clearances ?? s?.clearance ?? 0 },
  { key: 'foul', label: 'Foul', icon: 'ph:warning-bold', bg: 'bg-red-50', text: 'text-red-500', getValue: (s) => s?.fouls ?? s?.foul ?? 0 },
  { key: 'freestyle', label: 'Freestyle', icon: 'ph:sparkle-bold', bg: 'bg-purple-50', text: 'text-purple-600', getValue: (s) => s?.freestyle ?? s?.freestyles ?? 0 },
];

type InvasionSportType = 'minisoccer' | 'sepakbola' | 'futsal';

const VALID_INVASION_KEYS = ALL_METRICS_LIST.map((m) => m.key);

const sanitizeInvasionKeys = (keys: any, defaultKeys: string[]) => {
  if (!keys || !Array.isArray(keys)) return defaultKeys;
  const filtered = keys.filter((k) => typeof k === 'string' && VALID_INVASION_KEYS.includes(k));
  if (filtered.length === 4) return filtered;
  const result = [...filtered];
  for (const dKey of defaultKeys) {
    if (result.length >= 4) break;
    if (!result.includes(dKey)) result.push(dKey);
  }
  return result;
};

export const ClientStatsSettingsPage: React.FC = () => {
  const navigate = useNavigate();

  const [profile, setProfile] = useState<DataProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [activeSport, setActiveSport] = useState<InvasionSportType>('futsal');
  const [sportConfigs, setSportConfigs] = useState<Record<InvasionSportType, string[]>>({
    minisoccer: ['gol', 'assist', 'cleansheet', 'freestyle'],
    sepakbola: ['gol', 'assist', 'key_pass', 'cleansheet'],
    futsal: ['gol', 'assist', 'key_pass', 'cleansheet'],
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const res = await getClientProfile();
        if (res.success && res.data) {
          setProfile(res.data);
          const backendConfig = res.data.information?.stat_config || {};

          setSportConfigs({
            minisoccer: sanitizeInvasionKeys(
              backendConfig?.minisoccer || JSON.parse(localStorage.getItem('sporto_stat_config_minisoccer') || 'null'),
              ['gol', 'assist', 'cleansheet', 'freestyle']
            ),
            sepakbola: sanitizeInvasionKeys(
              backendConfig?.sepakbola || JSON.parse(localStorage.getItem('sporto_stat_config_sepakbola') || 'null'),
              ['gol', 'assist', 'key_pass', 'cleansheet']
            ),
            futsal: sanitizeInvasionKeys(
              backendConfig?.futsal || JSON.parse(localStorage.getItem('sporto_stat_config_futsal') || 'null'),
              ['gol', 'assist', 'key_pass', 'cleansheet']
            ),
          });
        }
      } catch (err) {
        console.error('Failed to load profile for stats settings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  if (profile?.profile_type === 'racket') {
    return <ClientRacketStatsSettingsPage initialProfile={profile} />;
  }

  const availableSportTabs: { id: InvasionSportType; label: string }[] = [
    { id: 'sepakbola', label: 'Sepak bola' },
    { id: 'futsal', label: 'Futsal' },
    { id: 'minisoccer', label: 'Mini soccer' },
  ];

  const currentKeys = sportConfigs[activeSport] || ['gol', 'assist', 'save', 'foul'];
  const sportStatItem = profile?.sport_stats?.[activeSport];

  const handleToggleMetric = (key: string) => {
    const isSelected = currentKeys.includes(key);
    let newKeys: string[];

    if (isSelected) {
      newKeys = currentKeys.filter((k) => k !== key);
    } else {
      if (currentKeys.length >= 4) {
        showToast.error('Maksimal pilih 4 metrik statistik');
        return;
      }
      newKeys = [...currentKeys, key];
    }

    setSportConfigs((prev) => ({
      ...prev,
      [activeSport]: newKeys,
    }));
  };

  const handleMoveUp = (index: number) => {
    if (index <= 0) return;
    const newSelected = [...currentKeys];
    const temp = newSelected[index - 1];
    newSelected[index - 1] = newSelected[index];
    newSelected[index] = temp;

    setSportConfigs((prev) => ({
      ...prev,
      [activeSport]: newSelected,
    }));
  };

  const handleMoveDown = (index: number) => {
    if (index >= currentKeys.length - 1) return;
    const newSelected = [...currentKeys];
    const temp = newSelected[index + 1];
    newSelected[index + 1] = newSelected[index];
    newSelected[index] = temp;

    setSportConfigs((prev) => ({
      ...prev,
      [activeSport]: newSelected,
    }));
  };

  const handleSave = async () => {
    for (const sport of ['minisoccer', 'sepakbola', 'futsal'] as const) {
      if (sportConfigs[sport].length !== 4) {
        const sportName = sport === 'minisoccer' ? 'Mini soccer' : sport === 'sepakbola' ? 'Sepak bola' : 'Futsal';
        showToast.error(`Pilih tepat 4 metrik untuk ${sportName}`);
        return;
      }
    }

    try {
      setIsSaving(true);
      const updatedInfo = {
        ...(profile?.information || {}),
        stat_config: {
          ...(profile?.information?.stat_config || {}),
          ...sportConfigs,
        },
      };

      await updateClientProfile({
        information: updatedInfo,
      });

      localStorage.setItem('sporto_stat_config_minisoccer', JSON.stringify(sportConfigs.minisoccer));
      localStorage.setItem('sporto_stat_config_sepakbola', JSON.stringify(sportConfigs.sepakbola));
      localStorage.setItem('sporto_stat_config_futsal', JSON.stringify(sportConfigs.futsal));

      showToast.success('Pengaturan statistik berhasil disimpan!');
      setTimeout(() => {
        navigate(-1);
      }, 500);
    } catch (err: any) {
      showToast.error(err?.response?.data?.message || 'Gagal menyimpan statistik');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-[#F8FAFC] min-h-screen text-[#0E1726] pb-28 select-none flex flex-col">
      <SEO title="Informasi Statistik | Sporto" description="Pengaturan metrik statistik profil Sporto" />

      {/* Desktop Header */}
      <div className="hidden md:flex items-center gap-3 max-w-md w-full mx-auto pt-4 pb-2 px-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full bg-white border border-[#E0E6ED] flex items-center justify-center text-[#0E1726] hover:bg-gray-50 transition-colors cursor-pointer shadow-2xs"
        >
          <Icon icon="ph:arrow-left-bold" className="w-5 h-5 text-[#0E1726]" />
        </button>
        <h1 className="text-lg md:text-xl font-bold text-[#0E1726]">Informasi Statistik</h1>
      </div>

      {/* Main Content */}
      <div className="max-w-md w-full mx-auto px-4 pt-3 flex-1 space-y-5 animate-fade-in">
        {/* Template Olahraga Section */}
        <div className="space-y-2">
          <label className="text-xs font-bold text-gray-500 block">Template olahraga</label>
          <div className="grid grid-cols-3 gap-2">
            {availableSportTabs.map((sport) => {
              const isActive = activeSport === sport.id;
              return (
                <button
                  key={sport.id}
                  type="button"
                  onClick={() => setActiveSport(sport.id)}
                  className={`h-11 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center justify-center ${
                    isActive
                      ? 'bg-[#FC480D] text-white shadow-sm font-extrabold'
                      : 'bg-white text-[#0E1726] border border-[#E0E6ED] hover:bg-gray-50 font-semibold'
                  }`}
                >
                  {sport.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Metrik Selection Section */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-gray-500">
              Pilih tepat 4 metrik statistik ({currentKeys.length}/4)
            </span>
          </div>

          <div className="space-y-2.5">
            {ALL_METRICS_LIST.map((metric) => {
              const isChecked = currentKeys.includes(metric.key);
              const val = metric.getValue(sportStatItem);

              return (
                <div
                  key={metric.key}
                  onClick={() => handleToggleMetric(metric.key)}
                  className={`bg-white rounded-2xl p-3.5 border transition-all flex items-center justify-between cursor-pointer ${
                    isChecked
                      ? 'border-gray-200 shadow-2xs'
                      : 'border-[#E0E6ED] opacity-70 hover:opacity-100'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {/* Checkbox */}
                    <div
                      className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors shrink-0 ${
                        isChecked ? 'bg-[#FC480D] text-white' : 'border border-gray-300 bg-white'
                      }`}
                    >
                      {isChecked && <Icon icon="ph:check-bold" className="w-3.5 h-3.5 stroke-[3]" />}
                    </div>

                    {/* Metric Icon */}
                    <div className={`w-9 h-9 rounded-xl ${metric.bg} ${metric.text} flex items-center justify-center shrink-0`}>
                      <Icon icon={metric.icon} className="w-5 h-5" />
                    </div>

                    {/* Metric Label */}
                    <span className={`text-sm font-bold truncate ${isChecked ? 'text-[#0E1726]' : 'text-gray-500'}`}>
                      {metric.label}
                    </span>
                  </div>

                  {/* Value */}
                  <span className="text-sm font-bold text-gray-400">
                    {loading ? '-' : val}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Urutan Tampil Section */}
        {currentKeys.length > 0 && (
          <div className="space-y-2 pt-2">
            <span className="text-xs font-bold text-gray-500 block">Urutan tampil</span>

            <div className="bg-white rounded-2xl border border-[#E0E6ED] shadow-2xs divide-y divide-gray-100 overflow-hidden">
              {currentKeys.map((key, idx) => {
                const metricDef = ALL_METRICS_LIST.find((m) => m.key === key);
                if (!metricDef) return null;

                return (
                  <div key={key} className="p-3.5 flex items-center justify-between">
                    <span className="text-sm font-bold text-[#0E1726]">{metricDef.label}</span>

                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleMoveUp(idx)}
                        disabled={idx === 0}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors cursor-pointer"
                        aria-label="Geser ke atas"
                      >
                        <Icon icon="ph:caret-up-bold" className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveDown(idx)}
                        disabled={idx === currentKeys.length - 1}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors cursor-pointer"
                        aria-label="Geser ke bawah"
                      >
                        <Icon icon="ph:caret-down-bold" className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Sticky Bottom Save Button */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-[#E0E6ED] px-4 pt-4 pb-safe-4 shadow-lg">
        <div className="max-w-md mx-auto">
          <button
            type="button"
            onClick={handleSave}
            disabled={currentKeys.length !== 4 || isSaving}
            className={`w-full h-12 rounded-full font-bold text-xs flex items-center justify-center gap-2 transition-all shadow-md ${
              currentKeys.length === 4 && !isSaving
                ? 'bg-[#FC480D] hover:bg-[#D83B08] text-white cursor-pointer active:scale-98 shadow-orange-200'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
            }`}
          >
            {isSaving ? (
              <>
                <Icon icon="ph:spinner-gap-bold" className="w-4.5 h-4.5 animate-spin" />
                <span>Menyimpan Perubahan...</span>
              </>
            ) : (
              <span>Simpan perubahan</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientStatsSettingsPage;
